import * as THREE from 'three';
import { ATMO, DETECTION, HUD, MISSILE_MANPADS, MISSILE_SHORAD, SIM } from '../config/tuning';
import { Atmosphere } from '../flight/Atmosphere';
import { Terrain } from '../world/Terrain';
import { dopplerVisible, radialVelocityMs, type RadarTarget } from '../threat/Detection';
import { Missile, MissileOutcome } from '../threat/Missile';
import { Countermeasures } from '../threat/Countermeasures';
import { createSeekerContext } from '../threat/Seeker';

/**
 * The intercept tuning harness (`?scene=intercept`, an M6 implementation note).
 *
 * One site, one missile, and every parameter that decides whether the player lives on a slider.
 * The engagement is run headless at the real 120 Hz through the real `Missile`, then drawn as a
 * top-down plot — because the thing being tuned is *geometry over time*, and geometry over time
 * is invisible from a nose camera. Flying the same intercept forty times to feel out where the
 * break window sits is exactly the work this page exists to delete.
 *
 * The plot shows both tracks, the point of closest approach, and where the aircraft was inside
 * the site's Doppler notch — the last of which is the whole mechanism by which a break turn
 * defeats a command-guided round (SPEC §3.6.5).
 */

const PANEL_STYLE = `
  position: fixed; top: 12px; left: 12px; z-index: 20;
  font: 11px/1.5 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  letter-spacing: 0.04em; text-transform: uppercase;
  color: ${HUD.colour}; background: rgba(4, 10, 12, 0.86);
  border: 1px solid rgba(63, 232, 216, 0.35); border-radius: 2px;
  padding: 10px 12px; width: 260px; max-height: calc(100vh - 24px); overflow-y: auto;
`;

const READOUT_STYLE = `
  position: fixed; top: 12px; right: 12px; z-index: 20;
  font: 12px/1.7 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  letter-spacing: 0.04em; text-transform: uppercase; white-space: pre;
  color: ${HUD.colour}; background: rgba(4, 10, 12, 0.86);
  border: 1px solid rgba(63, 232, 216, 0.35); border-radius: 2px;
  padding: 10px 14px;
`;

interface Slider {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  unit?: string;
}

const SLIDERS = {
  launchRange: { label: 'LAUNCH RANGE', min: 1000, max: 16000, step: 250, value: 6000, unit: ' M' },
  targetSpeed: { label: 'TARGET SPEED', min: 40, max: 130, step: 1, value: 90, unit: ' M/S' },
  altitude: { label: 'ALTITUDE', min: 50, max: 3000, step: 25, value: 600, unit: ' M' },
  breakAt: { label: 'BREAK AT T-', min: 0, max: 8, step: 0.1, value: 3.5, unit: ' S' },
  breakG: { label: 'BREAK G', min: 1, max: 8, step: 0.1, value: 6 },
  rollBack: { label: 'ROLL OUT AFTER', min: 0, max: 10, step: 0.1, value: 10, unit: ' S' },
  chaffAt: { label: 'CHAFF AT T-', min: 0, max: 8, step: 0.1, value: 0, unit: ' S' },
  flareAt: { label: 'FLARE AT T-', min: 0, max: 8, step: 0.1, value: 0, unit: ' S' },
} as const satisfies Record<string, Slider>;

type SliderKey = keyof typeof SLIDERS;

interface Sample {
  targetX: number;
  targetZ: number;
  missileX: number;
  missileZ: number;
  notched: boolean;
  lockState: string;
}

interface Result {
  outcome: string;
  missDistanceM: number;
  timeS: number;
  finalSpeedMs: number;
  peakSpeedMs: number;
  notchedS: number;
  lossCause: string;
  samples: Sample[];
  siteX: number;
  siteZ: number;
}

export function startInterceptScene(canvas: HTMLCanvasElement): void {
  const values = {} as Record<SliderKey, number>;
  for (const key of Object.keys(SLIDERS) as SliderKey[]) values[key] = SLIDERS[key].value;
  let seekerType: 'radar-command' | 'ir' = 'radar-command';

  const readout = document.createElement('div');
  readout.setAttribute('style', READOUT_STYLE);
  document.body.appendChild(readout);

  const context = canvas.getContext('2d');
  if (context === null) throw new Error('InterceptScene: 2D context unavailable');

  const run = (): void => {
    const result = simulate(values, seekerType);
    draw(context, canvas, result);
    readout.textContent =
      `OUTCOME    ${result.outcome.toUpperCase()}\n` +
      `MISS       ${result.missDistanceM > 9999 ? '>9999' : result.missDistanceM.toFixed(1)} M\n` +
      `FUSE       ${(seekerType === 'ir' ? MISSILE_MANPADS : MISSILE_SHORAD).fuseRadiusM} M\n` +
      `FLIGHT     ${result.timeS.toFixed(2)} S\n` +
      `PEAK SPEED ${result.peakSpeedMs.toFixed(0)} M/S\n` +
      `IMPACT SPD ${result.finalSpeedMs.toFixed(0)} M/S\n` +
      `NOTCHED    ${result.notchedS.toFixed(2)} S\n` +
      `LOCK LOST  ${result.lossCause.toUpperCase()}`;
  };

  buildPanel(values, run, (type) => {
    seekerType = type;
    run();
  });

  const resize = (): void => {
    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;
    run();
  };
  window.addEventListener('resize', resize);
  resize();
}

/** Fly the engagement headless at the simulation tick rate. */
function simulate(
  values: Record<SliderKey, number>,
  seekerType: 'radar-command' | 'ir',
): Result {
  const dtS = SIM.FIXED_DT;
  const resolution = 33;
  const terrain = new Terrain(
    { resolution, worldSizeM: 80000, minHeightM: 0, heightStepM: 0.01 },
    new Float32Array(resolution * resolution),
  );
  const atmosphere = new Atmosphere(SIM.DEFAULT_SEED);

  const target: RadarTarget = {
    positionM: new THREE.Vector3(0, values.altitude, 0),
    velocityMs: new THREE.Vector3(values.targetSpeed, 0, 0),
    forward: new THREE.Vector3(1, 0, 0),
    rcsM2: 0.35,
    altitudeAglM: values.altitude,
  };

  const siteM = new THREE.Vector3(values.launchRange * 0.9, 12, values.launchRange * 0.45);
  const params = seekerType === 'ir' ? MISSILE_MANPADS : MISSILE_SHORAD;

  const missile = new Missile();
  missile.launch(0, 0, 'X-1', params, siteM, target.positionM, 0x1234);

  const countermeasures = new Countermeasures();
  const seekerContext = createSeekerContext(target, terrain);
  seekerContext.decoys = countermeasures.active;

  const axis = new THREE.Vector3(0, 1, 0);
  const toSite = new THREE.Vector3();
  const beam = new THREE.Vector3();
  const samples: Sample[] = [];

  let breaking = false;
  let breakElapsedS = 0;
  let turnSign = 0;
  let notchedS = 0;
  let elapsedS = 0;
  let peakSpeedMs = 0;
  let chaffDone = false;
  let flareDone = false;

  for (let step = 0; step < 4000; step += 1) {
    const timeToGoS = missile.timeToGoS;

    // Countermeasure releases, on the same time-to-go clock as the break.
    if (!chaffDone && values.chaffAt > 0 && timeToGoS > 0 && timeToGoS <= values.chaffAt) {
      countermeasures.releaseChaff(target.positionM, target.velocityMs);
      chaffDone = true;
    }
    if (!flareDone && values.flareAt > 0 && timeToGoS > 0 && timeToGoS <= values.flareAt) {
      countermeasures.releaseFlare(target.positionM, target.velocityMs);
      flareDone = true;
    }

    if (values.breakAt > 0 && (breaking || (timeToGoS > 0 && timeToGoS <= values.breakAt))) {
      if (!breaking) {
        breaking = true;
        toSite.copy(siteM).sub(target.positionM);
        toSite.y = 0;
        toSite.normalize();
        beam.crossVectors(axis, toSite).normalize();
        turnSign = beam.dot(target.velocityMs) >= 0 ? 1 : -1;
      }
      breakElapsedS += dtS;

      const speed = target.velocityMs.length();
      const stepRad = ((values.breakG * ATMO.G) / Math.max(speed, 1)) * dtS;
      const rollingOut = breakElapsedS >= values.rollBack;

      // Toward the beam while breaking, back to the original heading once rolling out.
      if (rollingOut) {
        beam.set(1, 0, 0);
      } else {
        toSite.copy(siteM).sub(target.positionM);
        toSite.y = 0;
        toSite.normalize();
        beam.crossVectors(axis, toSite).multiplyScalar(turnSign).normalize();
      }

      const currentAngle = Math.atan2(target.velocityMs.z, target.velocityMs.x);
      const wantedAngle = Math.atan2(beam.z, beam.x);
      let delta = wantedAngle - currentAngle;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      if (Math.abs(delta) > 1e-4) {
        const applied = Math.min(Math.abs(delta), stepRad) * Math.sign(delta);
        target.velocityMs.applyAxisAngle(axis, -applied);
        target.forward.copy(target.velocityMs).normalize();
      }
    }

    target.positionM.addScaledVector(target.velocityMs, dtS);
    countermeasures.tick(dtS);

    const radial = radialVelocityMs(target.velocityMs, target.positionM, siteM);
    const siteHasTrack =
      dopplerVisible(radial, DETECTION.notchVelocityMs) >= DETECTION.notchDetectThreshold;
    if (!siteHasTrack) notchedS += dtS;

    if (step % 6 === 0) {
      samples.push({
        targetX: target.positionM.x,
        targetZ: target.positionM.z,
        missileX: missile.positionM.x,
        missileZ: missile.positionM.z,
        notched: !siteHasTrack,
        lockState: missile.seeker.state,
      });
    }

    peakSpeedMs = Math.max(peakSpeedMs, missile.speedMs);
    const outcome = missile.tick(dtS, seekerContext, atmosphere, siteHasTrack);
    elapsedS += dtS;

    if (outcome !== MissileOutcome.Flying) {
      return {
        outcome,
        missDistanceM: missile.missDistanceM,
        timeS: elapsedS,
        finalSpeedMs: missile.speedMs,
        peakSpeedMs,
        notchedS,
        lossCause: missile.seeker.lossCause,
        samples,
        siteX: siteM.x,
        siteZ: siteM.z,
      };
    }
  }

  return {
    outcome: 'timeout',
    missDistanceM: missile.missDistanceM,
    timeS: elapsedS,
    finalSpeedMs: missile.speedMs,
    peakSpeedMs,
    notchedS,
    lossCause: missile.seeker.lossCause,
    samples,
    siteX: siteM.x,
    siteZ: siteM.z,
  };
}

/** Top-down plot of both tracks. North is up; the site is a cross. */
function draw(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, result: Result): void {
  const width = canvas.width;
  const height = canvas.height;
  ctx.fillStyle = '#05080b';
  ctx.fillRect(0, 0, width, height);
  if (result.samples.length === 0) return;

  // Fit both tracks and the site into the frame.
  let minX = result.siteX;
  let maxX = result.siteX;
  let minZ = result.siteZ;
  let maxZ = result.siteZ;
  for (const sample of result.samples) {
    minX = Math.min(minX, sample.targetX, sample.missileX);
    maxX = Math.max(maxX, sample.targetX, sample.missileX);
    minZ = Math.min(minZ, sample.targetZ, sample.missileZ);
    maxZ = Math.max(maxZ, sample.targetZ, sample.missileZ);
  }
  const pad = 400;
  const scale = Math.min(
    (width - 320) / Math.max(maxX - minX + pad * 2, 1),
    (height - 120) / Math.max(maxZ - minZ + pad * 2, 1),
  );
  const originX = width * 0.5 - ((minX + maxX) * 0.5) * scale;
  const originY = height * 0.5 + ((minZ + maxZ) * 0.5) * scale;
  const px = (x: number): number => originX + x * scale;
  const py = (z: number): number => originY - z * scale;

  // Scale bar, so the geometry can be read in metres rather than in pixels.
  const barM = 1000;
  ctx.strokeStyle = 'rgba(63, 232, 216, 0.4)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(40, height - 40);
  ctx.lineTo(40 + barM * scale, height - 40);
  ctx.stroke();
  ctx.fillStyle = 'rgba(63, 232, 216, 0.7)';
  ctx.font = '11px ui-monospace, monospace';
  ctx.fillText('1 KM', 40, height - 48);

  // Site.
  ctx.strokeStyle = '#FFB000';
  ctx.beginPath();
  ctx.moveTo(px(result.siteX) - 7, py(result.siteZ));
  ctx.lineTo(px(result.siteX) + 7, py(result.siteZ));
  ctx.moveTo(px(result.siteX), py(result.siteZ) - 7);
  ctx.lineTo(px(result.siteX), py(result.siteZ) + 7);
  ctx.stroke();

  // Missile track.
  ctx.strokeStyle = '#FF3B30';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  result.samples.forEach((sample, index) => {
    const x = px(sample.missileX);
    const y = py(sample.missileZ);
    if (index === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();

  // Target track: cyan when the site holds a return, white while notched.
  ctx.lineWidth = 2;
  for (let i = 1; i < result.samples.length; i += 1) {
    const previous = result.samples[i - 1];
    const sample = result.samples[i];
    ctx.strokeStyle = sample.notched ? '#FFFFFF' : HUD.colour;
    ctx.beginPath();
    ctx.moveTo(px(previous.targetX), py(previous.targetZ));
    ctx.lineTo(px(sample.targetX), py(sample.targetZ));
    ctx.stroke();
  }

  // Closest approach.
  const last = result.samples[result.samples.length - 1];
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(px(last.targetX), py(last.targetZ), 6, 0, Math.PI * 2);
  ctx.stroke();

  // Below the scale bar, clear of the control panel in the top-left corner.
  ctx.fillStyle = 'rgba(63, 232, 216, 0.75)';
  ctx.fillText('CYAN: TRACKED   WHITE: NOTCHED   RED: MISSILE', 40, height - 16);
}

function buildPanel(
  values: Record<SliderKey, number>,
  onChange: () => void,
  onSeeker: (type: 'radar-command' | 'ir') => void,
): void {
  const panel = document.createElement('div');
  panel.setAttribute('style', PANEL_STYLE);

  const title = document.createElement('div');
  title.textContent = 'INTERCEPT · ?scene=intercept';
  title.style.opacity = '0.65';
  title.style.marginBottom = '8px';
  panel.appendChild(title);

  for (const key of Object.keys(SLIDERS) as SliderKey[]) {
    const spec: Slider = SLIDERS[key];
    const row = document.createElement('label');
    row.style.display = 'block';
    row.style.marginBottom = '6px';

    const caption = document.createElement('div');
    caption.style.display = 'flex';
    caption.style.justifyContent = 'space-between';
    caption.style.opacity = '0.8';
    const name = document.createElement('span');
    name.textContent = spec.label;
    const value = document.createElement('span');
    const format = (v: number): string =>
      `${spec.step < 1 ? v.toFixed(1) : v.toFixed(0)}${spec.unit ?? ''}`;
    value.textContent = format(values[key]);
    caption.append(name, value);

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = String(spec.min);
    slider.max = String(spec.max);
    slider.step = String(spec.step);
    slider.value = String(values[key]);
    slider.style.width = '100%';
    slider.style.accentColor = HUD.colour;
    slider.addEventListener('input', () => {
      values[key] = Number(slider.value);
      value.textContent = format(values[key]);
      onChange();
    });

    row.append(caption, slider);
    panel.appendChild(row);
  }

  const seekerRow = document.createElement('div');
  seekerRow.style.marginTop = '8px';
  for (const [label, type] of [
    ['RADAR COMMAND', 'radar-command'],
    ['INFRARED', 'ir'],
  ] as const) {
    const button = document.createElement('button');
    button.textContent = label;
    button.style.cssText =
      `display: block; width: 100%; margin-top: 4px; padding: 4px; cursor: pointer;` +
      `background: rgba(63,232,216,0.1); color: ${HUD.colour};` +
      `border: 1px solid rgba(63,232,216,0.4); font: inherit; text-transform: uppercase;`;
    button.addEventListener('click', () => onSeeker(type));
    seekerRow.appendChild(button);
  }
  panel.appendChild(seekerRow);

  const help = document.createElement('div');
  help.style.opacity = '0.5';
  help.style.marginTop = '8px';
  help.textContent = 'HOLD THE BEAM TO BREAK COMMAND GUIDANCE. ROLL OUT AND THE SITE RE-ACQUIRES.';
  panel.appendChild(help);

  document.body.appendChild(panel);
}
